/**
 * Workspace Usage Tab - 사용량 통계
 */

import { Trans } from '@lingui/macro';
import { BarChart3, Loader2 } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { cn } from '../../../lib/utils';
import { UsageStats, UsageLimit, formatBytes, UNLIMITED } from '../../../models/UsageStats';
import { getAdminFunc } from '../../../services/SuperAdminFirebase';

interface Props {
    workspaceId: string;
}

const WorkspaceUsageTab: React.FC<Props> = ({ workspaceId }) => {
    const [loading, setLoading] = useState(true);
    const [usageStats, setUsageStats] = useState<UsageStats | null>(null);
    const [usageLimits, setUsageLimits] = useState<UsageLimit | null>(null);

    useEffect(() => {
        loadUsageStats();
    }, [workspaceId]);

    const loadUsageStats = async () => {
        setLoading(true);
        try {
            const getUsage = getAdminFunc('getWorkspaceUsage');
            const result = await getUsage({ workspaceId });
            const data = result.data as { usage: UsageStats; limits: UsageLimit };
            setUsageStats(data.usage);
            setUsageLimits(data.limits);
        } catch (error) {
            console.error('Failed to load usage stats:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Card>
                <CardContent className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    <Trans>Usage Statistics</Trans>
                </CardTitle>
                <CardDescription>
                    <Trans>Current resource usage and limits</Trans>
                </CardDescription>
            </CardHeader>
            <CardContent>
                {usageStats && usageLimits ? (
                    <div className="space-y-6">
                        {/* 한도 경고 배너 */}
                        {(() => {
                            const storagePercent = usageLimits.storageBytes > 0
                                ? (usageStats.storageBytes / usageLimits.storageBytes) * 100 : 0;
                            const downloadPercent = usageLimits.monthlyDownloadBytes > 0
                                ? (usageStats.monthlyDownloadBytes / usageLimits.monthlyDownloadBytes) * 100 : 0;

                            const exceeded = storagePercent >= 100 || downloadPercent >= 100;
                            const warning = storagePercent >= 80 || downloadPercent >= 80;

                            if (exceeded) {
                                return (
                                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                                        <div className="flex items-center gap-2 text-red-700 font-medium">
                                            ⚠️ <Trans>Resource limit exceeded</Trans>
                                        </div>
                                        <div className="text-sm text-red-600 mt-1">
                                            {storagePercent >= 100 && <div>・ストレージが上限に達しました</div>}
                                            {downloadPercent >= 100 && <div>・月間ダウンロードが上限に達しました</div>}
                                        </div>
                                        <div className="text-xs text-red-500 mt-2">
                                            プランをアップグレードするか、不要なファイルを削除してください。
                                        </div>
                                    </div>
                                );
                            } else if (warning) {
                                return (
                                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                                        <div className="flex items-center gap-2 text-amber-700 font-medium">
                                            ⚡ <Trans>Resources are approaching the limit</Trans>
                                        </div>
                                        <div className="text-sm text-amber-600 mt-1">
                                            {storagePercent >= 80 && <div>・ストレージ使用率: {storagePercent.toFixed(0)}%</div>}
                                            {downloadPercent >= 80 && <div>・月間ダウンロード使用率: {downloadPercent.toFixed(0)}%</div>}
                                        </div>
                                    </div>
                                );
                            }
                            return null;
                        })()}

                        {/* Storage */}
                        <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                                <span className="text-slate-600"><Trans>Storage</Trans></span>
                                <span className="font-medium">
                                    {formatBytes(usageStats.storageBytes)} / {usageLimits.storageBytes === UNLIMITED ? '無制限' : formatBytes(usageLimits.storageBytes)}
                                </span>
                            </div>
                            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                <div
                                    className={cn(
                                        "h-full rounded-full transition-all",
                                        usageLimits.storageBytes > 0 && (usageStats.storageBytes / usageLimits.storageBytes) > 0.9
                                            ? "bg-red-500"
                                            : usageLimits.storageBytes > 0 && (usageStats.storageBytes / usageLimits.storageBytes) > 0.7
                                                ? "bg-yellow-500"
                                                : "bg-indigo-500"
                                    )}
                                    style={{
                                        width: usageLimits.storageBytes === UNLIMITED ? '5%'
                                            : `${Math.min((usageStats.storageBytes / usageLimits.storageBytes) * 100, 100)}%`
                                    }}
                                />
                            </div>
                        </div>

                        {/* Monthly Download */}
                        <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                                <span className="text-slate-600"><Trans>Monthly Download</Trans></span>
                                <span className="font-medium">
                                    {formatBytes(usageStats.monthlyDownloadBytes)} / {usageLimits.monthlyDownloadBytes === UNLIMITED ? '無制限' : formatBytes(usageLimits.monthlyDownloadBytes)}
                                </span>
                            </div>
                            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                <div
                                    className={cn(
                                        "h-full rounded-full transition-all",
                                        usageLimits.monthlyDownloadBytes > 0 && (usageStats.monthlyDownloadBytes / usageLimits.monthlyDownloadBytes) > 0.9
                                            ? "bg-red-500"
                                            : usageLimits.monthlyDownloadBytes > 0 && (usageStats.monthlyDownloadBytes / usageLimits.monthlyDownloadBytes) > 0.7
                                                ? "bg-yellow-500"
                                                : "bg-emerald-500"
                                    )}
                                    style={{
                                        width: usageLimits.monthlyDownloadBytes === UNLIMITED ? '5%'
                                            : `${Math.min((usageStats.monthlyDownloadBytes / usageLimits.monthlyDownloadBytes) * 100, 100)}%`
                                    }}
                                />
                            </div>
                        </div>

                        {/* Counts */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t">
                            <div className="text-center p-3 bg-slate-50 rounded-lg">
                                <div className="text-2xl font-bold text-slate-900">{usageStats.memberCount}</div>
                                <div className="text-xs text-slate-500">
                                    <Trans>Members</Trans>
                                    {usageLimits.memberCount > 0 && <span className="ml-1">/ {usageLimits.memberCount}</span>}
                                </div>
                            </div>
                            <div className="text-center p-3 bg-slate-50 rounded-lg">
                                <div className="text-2xl font-bold text-slate-900">{usageStats.channelCount}</div>
                                <div className="text-xs text-slate-500">
                                    <Trans>Channels</Trans>
                                    {usageLimits.channelCount > 0 && <span className="ml-1">/ {usageLimits.channelCount}</span>}
                                </div>
                            </div>
                            <div className="text-center p-3 bg-slate-50 rounded-lg">
                                <div className="text-2xl font-bold text-slate-900">{usageStats.botCount}</div>
                                <div className="text-xs text-slate-500">
                                    <Trans>Bots</Trans>
                                    {usageLimits.botCount > 0 && <span className="ml-1">/ {usageLimits.botCount}</span>}
                                </div>
                            </div>
                            <div className="text-center p-3 bg-slate-50 rounded-lg">
                                <div className="text-2xl font-bold text-slate-900">{usageStats.storageCount}</div>
                                <div className="text-xs text-slate-500"><Trans>Files</Trans></div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-12 text-slate-500">
                        <BarChart3 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                        <p><Trans>No usage data available</Trans></p>
                        <p className="text-sm mt-1"><Trans>Usage data will appear after workspace activity</Trans></p>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default WorkspaceUsageTab;
